import { useEffect } from 'react'
import Modal from '../Modal/Modal'
import Button from '../Button/Button'
import './ConfirmDialog.css'
import { useLanguage } from '@/hooks/useLanguage'
import { t } from '@/utils/i18n'

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  )
}

interface ConfirmDialogProps {
  isOpen: boolean
  message: string
  title?: string
  onConfirm: () => void
  onCancel: () => void
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'primary'
  isLoading?: boolean
}

function ConfirmDialog({
  isOpen,
  message,
  title: customTitle,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  variant = 'primary',
  isLoading = false,
}: ConfirmDialogProps) {
  const { language } = useLanguage()
  const confirmLabel = confirmText && confirmText.trim() !== '' ? confirmText : t('common.confirm', language)
  const cancelLabel = cancelText && cancelText.trim() !== '' ? cancelText : t('common.cancel', language)
  const isUpgradeStyle = variant === 'primary' && customTitle != null && customTitle !== ''

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={isLoading ? () => {} : onCancel} 
      title={isUpgradeStyle ? undefined : t('common.confirm', language)} 
      size="sm" 
      showCloseButton={false}
      className={`confirm-dialog-modal ${isUpgradeStyle ? 'confirm-dialog-modal--with-icon' : ''}`}
      overlayClassName="confirm-dialog-modal-overlay"
    >
      <div className={`confirm-dialog ${isLoading ? 'confirm-dialog--loading' : ''} ${isUpgradeStyle ? 'confirm-dialog--with-icon' : ''}`}>
        {isUpgradeStyle && (
          <div className="confirm-dialog__icon-wrap">
            <StarIcon className="confirm-dialog__icon" />
          </div>
        )}
        <div className="confirm-dialog__message-wrap">
          {isUpgradeStyle && customTitle && <h3 className="confirm-dialog__title">{customTitle}</h3>}
          <p className="confirm-dialog__message">{message}</p>
        </div>
        <div className="confirm-dialog__actions">
          <Button variant="ghost" onClick={onCancel} disabled={isLoading} className={isUpgradeStyle ? 'confirm-dialog__btn-cancel' : ''}>
            {cancelLabel}
          </Button>
          <Button variant={variant === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="confirm-dialog__loading-spinner"></span>
                {t('messages.processing', language)}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmDialog
